import React from 'react';
import { Button, DatePicker } from 'antd';
import { DownloadOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import './dailyReportDateBar.scss';

export type DateRange = [Dayjs, Dayjs];

interface DailyReportDateBarProps {
    // Currently selected date (controlled by the parent modal).
    date: Dayjs;
    onChange: (date: Dayjs) => void;
    onExport: () => void;
    isExporting: boolean;
    exportDisabled: boolean;
    // Opt in to the previous/next day arrows either side of the date controls.
    showDateArrows?: boolean;
    // Opt in to the optional range picker, for viewing several dates at once.
    // A range supersedes the single date, so the day controls are disabled while
    // one is set. Clearing it returns to the single day.
    range?: DateRange | null;
    onRangeChange?: (range: DateRange | null) => void;
}

const DATE_FORMAT = 'DD/MM/YYYY';

const { RangePicker } = DatePicker;

const DailyReportDateBar: React.FC<DailyReportDateBarProps> = ({
    date, onChange, onExport, isExporting, exportDisabled, showDateArrows, range, onRangeChange,
}) => {
    const today = dayjs().startOf('day');
    const tomorrow = today.add(1, 'day');
    const isSameDay = (a: Dayjs, b: Dayjs) => a.format('YYYY-MM-DD') === b.format('YYYY-MM-DD');
    const hasRange = Boolean(range);

    return (
        <div className="daily-report-date-bar">
            <div className="daily-report-date-bar__pickers">
                {showDateArrows && (
                    <Button
                        icon={<LeftOutlined />}
                        aria-label="Previous day"
                        disabled={hasRange}
                        onClick={() => onChange(date.subtract(1, 'day'))} />
                )}
                <Button
                    type={!hasRange && isSameDay(date, today) ? 'primary' : 'default'}
                    disabled={hasRange}
                    onClick={() => onChange(today)}>
                    Today
                </Button>
                <Button
                    type={!hasRange && isSameDay(date, tomorrow) ? 'primary' : 'default'}
                    disabled={hasRange}
                    onClick={() => onChange(tomorrow)}>
                    Tomorrow
                </Button>
                <DatePicker
                    allowClear={false}
                    value={date}
                    disabled={hasRange}
                    format={DATE_FORMAT}
                    popupClassName="date-picker-popup"
                    onChange={(value) => value && onChange(value.startOf('day'))} />
                {showDateArrows && (
                    <Button
                        icon={<RightOutlined />}
                        aria-label="Next day"
                        disabled={hasRange}
                        onClick={() => onChange(date.add(1, 'day'))} />
                )}
                {onRangeChange && (
                    <RangePicker
                        allowEmpty={[false, false]}
                        value={range ?? null}
                        format={DATE_FORMAT}
                        popupClassName="date-picker-popup"
                        placeholder={['Date range', 'to']}
                        onChange={(values) => {
                            const [from, to] = values ?? [null, null];
                            onRangeChange(from && to ? [from.startOf('day'), to.startOf('day')] : null);
                        }} />
                )}
            </div>
            <Button
                icon={<DownloadOutlined />}
                loading={isExporting}
                disabled={exportDisabled}
                onClick={onExport}>
                Export CSV
            </Button>
        </div>
    );
};

export default DailyReportDateBar;
